import React from 'react';
import {
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import { TimelineStatistics } from '../../types/timeline';

interface GenerationFilterProps {
  statistics: TimelineStatistics;
  selectedGenerations: number[];
  onChange: (generations: number[]) => void;
}

/**
 * GenerationFilter Component
 * Filter timeline events by generation with visual selector
 * Part of Phase 3.3: Interactive Timeline Page
 */
export const GenerationFilter: React.FC<GenerationFilterProps> = ({
  statistics,
  selectedGenerations,
  onChange,
}) => {
  // Get all available generations from statistics
  const availableGenerations = React.useMemo(() => {
    return Object.keys(statistics.eventsByGeneration)
      .map(g => parseInt(g))
      .sort((a, b) => a - b);
  }, [statistics]);

  const handleToggleGeneration = (generation: number) => {
    if (selectedGenerations.includes(generation)) {
      onChange(selectedGenerations.filter(g => g !== generation));
    } else {
      onChange([...selectedGenerations, generation]);
    }
  };

  const handleSelectAll = () => {
    onChange(availableGenerations);
  };

  const handleDeselectAll = () => {
    onChange([]);
  };

  if (availableGenerations.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          No generation data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Generations
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {selectedGenerations.length} / {availableGenerations.length} selected
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleSelectAll}
          disabled={selectedGenerations.length === availableGenerations.length}
        >
          Select All
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={handleDeselectAll}
          disabled={selectedGenerations.length === 0}
        >
          Clear
        </Button>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Generation Checkboxes with Event Count */}
      <FormGroup>
        {availableGenerations.map(generation => {
          const count = statistics.eventsByGeneration[generation] || 0;

          return (
            <FormControlLabel
              key={generation}
              control={
                <Checkbox
                  checked={selectedGenerations.includes(generation)}
                  onChange={() => handleToggleGeneration(generation)}
                  size="small"
                />
              }
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography variant="body2">
                    Generation {generation + 1} (G{generation + 1})
                  </Typography>
                  <Chip
                    label={count}
                    size="small"
                    color="primary"
                    sx={{
                      ml: 1,
                      height: '20px',
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
              }
              sx={{ mb: 0.5 }}
            />
          );
        })}
      </FormGroup>
    </Box>
  );
};

export default GenerationFilter;
